// VOICE-CHOICES (Andrei, 2026-11-02: "If they use it, we bill it. We can have the free voice …
// we want to give them a few voices so they select who they want to talk to in the settings and
// whatever they select, that's what we bill for."). The fixed list of stock assistant voices a
// person picks from (personal settings) or the owner sets as the company default (company
// settings). No cloning and no "your own voice": the list is ours, never uploaded.
//
// Andrei's final call (2026-11-02): the picker offers only Aiden and Ryan (Qwen3-TTS through his
// LiteLLM gateway), default Aiden. The other engines stay in the code behind configuration and
// are offered only once configured:
// - `self_hosted`: Qwen3-TTS through the gateway (`qwen_gateway`, model `Text2Voice`; STT
//   `whisper_gateway`, model `Voice2Text`), Orpheus 3B "tara" on a GPU endpoint. Billed at the
//   self-hosted rate (default 3¢ per minute).
// - `free` (CPU): Kokoro-82M TTS with Parakeet STT, OpenAI-compatible servers. The free rate
//   (default 0: included).
// - `premium` (key slots): ElevenLabs v4 Turbo (alternative Cartesia Sonic) with Deepgram STT.
//   Provider cost × the markup (default 1.0: at cost).
// Room for more: a designed voice (Qwen's VoiceDesign model) is one more `qwenVoice` line — a new
// speaker id on the same provider.
import { z } from "zod";

export const VoiceTier = z.enum(["free", "self_hosted", "premium"]);
export type VoiceTier = z.infer<typeof VoiceTier>;

export const VoiceId = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9-]{1,39}$/)
  .describe("A voice of the catalogue (`qwen-aiden`, `qwen-ryan`, …)");

/** The TTS engines and services a voice can be spoken by (each behind the provider interface). */
export const TTS_ENGINES = ["qwen_gateway", "kokoro", "orpheus", "elevenlabs", "cartesia"] as const;
export type TtsEngine = (typeof TTS_ENGINES)[number];
export const TtsEngineSchema = z.enum(TTS_ENGINES);

export type VoiceCatalogueEntry = {
  id: string;
  name: string;
  /** The line the picker plays as a preview (synthesized on demand by its engine, cached). */
  sample: string;
  tier: VoiceTier;
  /**
   * The engines that may speak it, in order of preference, each with the voice's name there
   * (a stock speaker, or for a premium service the env slot holding the operator's stock voice
   * id). The voice service takes the first one configured; none configured: not offered.
   */
  engines: readonly { engine: TtsEngine; voice: string }[];
  /** Who listens: the self-hosted STT (the gateway's, else Parakeet) or Deepgram. */
  stt: "local" | "deepgram";
  /**
   * Premium only: what one minute of a voice session costs us per TTS service, in micros (USD):
   * Deepgram Flux (list $0.0077/min) plus the service for the share of the minute the assistant
   * speaks (~half; ElevenLabs v4 Turbo ~ $0.034/min of speech, Cartesia Sonic ~ $0.038). List
   * estimates from the research (2026-11), checked before go-live.
   */
  cost_micros_per_minute?: Partial<Record<TtsEngine, number>>;
};

const SAMPLE = (name: string) =>
  `Hi, I'm ${name}. I can read you your morning brief and keep your team's work moving.`;

/** A Qwen3-TTS voice through the gateway (a stock or designed speaker id). */
export const qwenVoice = (speaker: string, name: string): VoiceCatalogueEntry => ({
  id: `qwen-${speaker.replace(/_/g, "-")}`,
  name,
  sample: SAMPLE(name),
  tier: "self_hosted",
  engines: [{ engine: "qwen_gateway", voice: speaker }],
  stt: "local",
});

export const VOICE_CATALOGUE: readonly VoiceCatalogueEntry[] = [
  qwenVoice("aiden", "Aiden"),
  qwenVoice("ryan", "Ryan"),
  // Behind configuration (not offered until their engine is set up).
  {
    id: "orpheus-tara",
    name: "Tara",
    sample: SAMPLE("Tara"),
    tier: "self_hosted",
    engines: [{ engine: "orpheus", voice: "tara" }],
    stt: "local",
  },
  {
    id: "kokoro-heart",
    name: "Heart",
    sample: SAMPLE("Heart"),
    tier: "free",
    engines: [{ engine: "kokoro", voice: "af_heart" }],
    stt: "local",
  },
  {
    id: "kokoro-michael",
    name: "Michael",
    sample: SAMPLE("Michael"),
    tier: "free",
    engines: [{ engine: "kokoro", voice: "am_michael" }],
    stt: "local",
  },
  {
    id: "premium-1",
    name: "Nova",
    sample: SAMPLE("Nova"),
    tier: "premium",
    engines: [
      { engine: "elevenlabs", voice: "ELEVENLABS_VOICE_ID" },
      { engine: "cartesia", voice: "CARTESIA_VOICE_ID" },
    ],
    stt: "deepgram",
    cost_micros_per_minute: { elevenlabs: 7_700 + 17_000, cartesia: 7_700 + 19_000 },
  },
];

/** The default voice (Andrei's call; the owner's `settings.voice.default_voice_id` overrides). */
export const DEFAULT_VOICE_ID = "qwen-aiden";

export function voiceById(id: string | null | undefined): VoiceCatalogueEntry | null {
  return VOICE_CATALOGUE.find((v) => v.id === id) ?? null;
}

/**
 * Mood (the speaking style). `plain` (default) sends no instructions; every other mood is one
 * sentence sent as the OpenAI-style `instructions` field on every speech request (inline tags
 * like `[excited]` are read aloud as words, so they are never sent: `speakable`). The person's
 * chosen mood is used for every reply; the agent never switches it.
 *
 * A mood makes replies slower: `speed_ratio` is the reply's duration against Plain on the fixed
 * test line (`MOOD_TEST_LINE`), from the gateway probe of 2026-11-02 (Plain 4.2 s, Energetic
 * 4.4 s, Serious 5.4 s, Calm 4.5–9.5 s by voice: its midpoint); Warm was not in the probe and is
 * an estimate until `services/voice` `measureMoods` is run against the gateway. The picker shows
 * `speedLabel(speed_ratio)` and, off Plain, `MOOD_SLOWER_NOTE`.
 */
export const Mood = z.enum(["plain", "warm", "energetic", "calm", "serious"]);
export type Mood = z.infer<typeof Mood>;

export const MOOD_TEST_LINE =
  "Good morning. Two cards moved overnight, one is waiting for your approval, and the pipeline has three new leads.";

export const MOODS: Readonly<
  Record<
    Mood,
    { label: string; instructions: string | null; speed_ratio: number; measured: boolean }
  >
> = {
  plain: { label: "Plain", instructions: null, speed_ratio: 1, measured: true },
  energetic: {
    label: "Energetic",
    instructions: "Speak with upbeat energy and enthusiasm, at a brisk but clear pace.",
    speed_ratio: 4.4 / 4.2,
    measured: true,
  },
  warm: {
    label: "Warm",
    instructions: "Speak in a warm, friendly and reassuring tone.",
    speed_ratio: 1.15,
    measured: false,
  },
  serious: {
    label: "Serious",
    instructions: "Speak in a serious, composed and matter-of-fact tone.",
    speed_ratio: 5.4 / 4.2,
    measured: true,
  },
  calm: {
    label: "Calm",
    instructions: "Speak calmly and steadily, at an unhurried pace with gentle pauses.",
    speed_ratio: 7.0 / 4.2,
    measured: true,
  },
};
export const DEFAULT_MOOD: Mood = "plain";
export const MOOD_SLOWER_NOTE = "Replies will take a little longer in this mood.";

/** The speed indicator for a duration ratio against Plain. */
export function speedLabel(ratio: number): string {
  if (ratio <= 1.01) return "fastest";
  if (ratio < 1.1) return "slightly slower";
  if (ratio < 1.5) return "slower";
  return "slowest";
}

/** The moods in speed order (fastest first), as the picker lists them. */
export function moodsBySpeed(): Mood[] {
  return Mood.options.slice().sort((a, b) => MOODS[a].speed_ratio - MOODS[b].speed_ratio);
}

/** Text as it may be spoken: bracket tags (`[laughs]`, `[excited]`) removed, spaces tidied. */
export function speakable(text: string): string {
  return text
    .replace(/\[[^\]\n]{0,40}\]/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +([,.!?;:])/g, "$1")
    .trim();
}

/**
 * The company's voice prices: the premium markup in basis points (10 000 = 1.0), the free
 * (CPU) rate and the self-hosted (GPU) rate per minute, in micros.
 */
export type VoiceRates = {
  markupBp: number;
  freeRateMicrosPerMinute: number;
  selfHostedRateMicrosPerMinute: number;
};
export const DEFAULT_VOICE_RATES: VoiceRates = {
  markupBp: 10_000,
  freeRateMicrosPerMinute: 0,
  selfHostedRateMicrosPerMinute: 30_000,
};

/** A premium voice's provider cost per minute: by its engine, else its most expensive one. */
export function costMicrosPerMinute(voice: VoiceCatalogueEntry, engine?: string | null): number {
  const costs = voice.cost_micros_per_minute ?? {};
  const own = engine ? costs[engine as TtsEngine] : undefined;
  return own ?? Math.max(0, ...Object.values(costs).filter((n): n is number => n !== undefined));
}

/** What one minute of `voice` (spoken by `engine`) bills the client, in micros. */
export function rateMicrosPerMinute(
  voice: VoiceCatalogueEntry,
  r: VoiceRates,
  engine?: string | null,
): number {
  if (voice.tier === "free") return Math.max(0, Math.round(r.freeRateMicrosPerMinute));
  if (voice.tier === "self_hosted") return Math.max(0, Math.round(r.selfHostedRateMicrosPerMinute));
  return Math.max(0, Math.round((costMicrosPerMinute(voice, engine) * r.markupBp) / 10_000));
}

/** A segment of `seconds` at `rate` per minute, rounded up to the micro. */
export function segmentMicros(rate: number, seconds: number): number {
  if (rate <= 0 || seconds <= 0) return 0;
  return Math.ceil((rate * seconds) / 60);
}

/** The picker's short price note: "included", or "$0.03 per minute". */
export function priceNote(rate: number): string {
  if (rate <= 0) return "included";
  const usd = rate / 1_000_000;
  const fixed = usd.toFixed(usd >= 0.1 ? 2 : usd >= 0.01 ? 3 : 4).replace(/(\.\d\d\d*?)0+$/, "$1");
  return `$${fixed} per minute`;
}
